// Phase OWL-G1 — Continent flag parsing (GAP 2).
//
// Reads owl_hour_slips.continent_flags JSONB. Supports two shapes:
//   1) Legacy flat: { altitude?: bool, rivalry?: bool, ... }
//   2) Brief-spec nested:
//      {
//        altitude: { active: bool, venues: [{ name, city, elevation_m }] },
//        rivalry:  { active: bool, matches: [{ home, away, match_name, league }] }
//      }
// Parser is defensive — never throws on malformed shapes.

export interface AltitudeVenue {
  name: string;
  city: string;
  elevation_m: number;
}

export interface RivalryMatch {
  home: string;
  away: string;
  match_name: string;
  league: string;
}

export interface ParsedContinentFlags {
  altitude: { active: boolean; venues: readonly AltitudeVenue[] };
  rivalry: { active: boolean; matches: readonly RivalryMatch[] };
}

function asNum(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function asStr(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function parseVenues(v: unknown): readonly AltitudeVenue[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map((row) => ({
      name: asStr(row.name),
      city: asStr(row.city),
      elevation_m: asNum(row.elevation_m),
    }));
}

function parseMatches(v: unknown): readonly RivalryMatch[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map((row) => ({
      home: asStr(row.home),
      away: asStr(row.away),
      match_name: asStr(row.match_name),
      league: asStr(row.league),
    }));
}

export function parseContinentFlags(raw: unknown): ParsedContinentFlags {
  const obj = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  // Nested shape (brief spec)
  const altRaw = obj.altitude;
  const rivRaw = obj.rivalry;

  let altActive = false;
  let altVenues: readonly AltitudeVenue[] = [];
  if (typeof altRaw === "object" && altRaw !== null) {
    const a = altRaw as Record<string, unknown>;
    altVenues = parseVenues(a.venues);
    altActive = a.active === true || altVenues.length > 0;
  } else if (altRaw === true) {
    // Legacy flat shape — flag present but no payload
    altActive = true;
  }

  let rivActive = false;
  let rivMatches: readonly RivalryMatch[] = [];
  if (typeof rivRaw === "object" && rivRaw !== null) {
    const r = rivRaw as Record<string, unknown>;
    rivMatches = parseMatches(r.matches);
    rivActive = r.active === true || rivMatches.length > 0;
  } else if (rivRaw === true) {
    rivActive = true;
  }

  return {
    altitude: { active: altActive, venues: altVenues },
    rivalry: { active: rivActive, matches: rivMatches },
  };
}

export const ALTITUDE_EMPTY_COPY = "No altitude fixtures tonight";
export const RIVALRY_EMPTY_COPY = "No high-rivalry matches tonight";
